import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@material-ui/core'
import { useEffect, useMemo, useState } from 'react'

import Loader from 'components/_shared/loader/Loader'
import { Device } from 'containers/Devices/services/devices-types'
import map from 'lodash/map'
import { useDispatch } from 'react-redux'
import { checkConnection } from 'services/root/root-service'
import { useAppSelector } from 'utils/reduxHelpers'
import devicesListActions from '../../services/devicesList-actions'
import { DeviceSubTypeCell } from './cells/DeviceSubTypeCell'
import { SerialNumberCell } from './cells/SerialNumberCell'

export const SensorTable = () => {
  const dispatch = useDispatch()
  const devices = useAppSelector(
    (state) => state.devicesReducer.devicesListReducer.devicesList
  )
  const isLoading = useAppSelector(
    (state) => state.devicesReducer.devicesListReducer.isLoading
  )
  const page = useAppSelector(
    (state) => state.devicesReducer.devicesListReducer.page
  )
  const rowsPerPage = useAppSelector(
    (state) => state.devicesReducer.devicesListReducer.rowsPerPage
  )
  const totalCount = useAppSelector(
    (state) => state.devicesReducer.devicesListReducer.totalCount
  )

  const filterState = useAppSelector(
    (state) => state.devicesReducer.devicesListReducer.filterState
  )

  const [sort, setSort] = useState<
    | 'serialNumber'
    | 'organizationName'
    | 'hardwareId'
    | 'nickname'
    | 'deviceSubType'
  >('serialNumber')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  useEffect(() => {
    setSort(filterState.sort || 'serialNumber')
    setSortDirection(filterState.sortDirection || 'asc')
  }, [filterState.sort, filterState.sortDirection])

  const toggleDeviceSort = (
    filterSort: typeof sort,
    filterSortDirection: typeof sortDirection
  ) => {
    dispatch(
      devicesListActions.toggleSort({
        sort: filterSort,
        sortDirection: filterSortDirection,
      })
    )
  }

  const goToNextPage = (page: number) =>
    dispatch(checkConnection(() => devicesListActions.goToNextPage(page)))
  const goToPreviousPage = (page: number) =>
    dispatch(devicesListActions.goToPreviousPage(page))
  const changeRowsPerPage = (value: number) =>
    dispatch(devicesListActions.setRowsPerPage(value))
  const changePage = (newPage: number) => {
    const isDevicesWasLoaded = devices.length <= newPage * rowsPerPage

    if (newPage > page && isDevicesWasLoaded) {
      goToNextPage(newPage)
    } else {
      goToPreviousPage(newPage)
    }
  }

  const toggleSort = (newSort: typeof sort) => {
    toggleDeviceSort(
      newSort,
      newSort === sort
        ? sortDirection === 'asc'
          ? 'desc'
          : 'asc'
        : sortDirection
    )
  }

  return (
    <Loader isLoading={isLoading}>
      <Table id="devices-table">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell
              sortDirection={sort === 'serialNumber' ? sortDirection : false}
            >
              <TableSortLabel
                active={sort === 'serialNumber'}
                direction={sortDirection}
                onClick={() => toggleSort('serialNumber')}
              >
                Serial Number
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={
                sort === 'organizationName' ? sortDirection : false
              }
            >
              <TableSortLabel
                active={sort === 'organizationName'}
                direction={sortDirection}
                onClick={() => toggleSort('organizationName')}
              >
                Organization
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={sort === 'hardwareId' ? sortDirection : false}
            >
              <TableSortLabel
                active={sort === 'hardwareId'}
                direction={sortDirection}
                onClick={() => toggleSort('hardwareId')}
              >
                Hardware ID
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={sort === 'deviceSubType' ? sortDirection : false}
            >
              <TableSortLabel
                active={sort === 'deviceSubType'}
                direction={sortDirection}
                onClick={() => toggleSort('deviceSubType')}
              >
                Type
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={sort === 'nickname' ? sortDirection : false}
            >
              <TableSortLabel
                active={sort === 'nickname'}
                direction={sortDirection}
                onClick={() => toggleSort('nickname')}
              >
                Nickname
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {useMemo(
            () =>
              map(devices, (device: Device) => (
                <TableRow key={`sensor-device-${device.id}`}>
                  <TableCell>{device.id}</TableCell>
                  <SerialNumberCell {...device} />
                  <TableCell>{device.organizationName}</TableCell>
                  <TableCell>{device.hardwareId}</TableCell>
                  <DeviceSubTypeCell {...device} />
                  <TableCell>{device.nickname}</TableCell>
                </TableRow>
              )),
            [devices]
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[10, 30, 50]}
              colSpan={6}
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => {
                changePage(newPage)
              }}
              onRowsPerPageChange={(event) => {
                changeRowsPerPage(parseInt(event.target.value, 10))
                changePage(0)
              }}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </Loader>
  )
}
